import { useMemo } from 'react';
import {
  useRetrieveLatestWorkspaceBooks,
  useRetrieveWorkspaceBook,
} from '@/generated/hooks/bdk/v2/services/library';
import { getLatestVersionIntegrations } from '@/modules/integrations/utils';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { FilterBuilder } from '@/shared/utils';
import { getIntegrationDisplayName } from '@/shared/utils/integrations-utils';
import { convertToDataUrl } from '@/shared/utils/image-utils';

/** How a book should be presented once resolved from its bare name. */
export interface IBookIdentity {
  /** Data-URL icon, or undefined when the book has none (fall back to initials). */
  icon?: string;
  /** Human-readable book name; the raw `bookName` until the lookup resolves. */
  displayName: string;
  /** True while either lookup is in flight. */
  isLoading: boolean;
}

/**
 * Resolves a book's icon and display name from the bare `book_name` a journal
 * event carries.
 *
 * Two hops, because an event names a book but not a version: find the latest
 * version by name, then retrieve it for the icon. React Query caches by key, so
 * repeated calls into one book cost a single pair of requests.
 *
 * @param bookName - Book name from `book_function.book_name`
 * @returns The book's icon, display name, and loading state
 */
export function useBookIdentity(bookName?: string): IBookIdentity {
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const { data: booksData, isLoading: isLoadingBooks } =
    useRetrieveLatestWorkspaceBooks(
      organizationId,
      workspaceId,
      {
        page_size: 100,
        filter: bookName
          ? FilterBuilder.create().equals('name', bookName).build()
          : undefined,
      },
      {
        query: {
          enabled: !!organizationId && !!workspaceId && !!bookName,
        },
      },
    );

  const latestBook = useMemo(() => {
    const books = booksData?.data?.books ?? [];
    if (books.length === 0) return null;
    return getLatestVersionIntegrations(books)[0] ?? null;
  }, [booksData]);

  const { data: bookData, isLoading: isLoadingBook } = useRetrieveWorkspaceBook(
    organizationId,
    workspaceId,
    bookName || '',
    latestBook?.version || '',
    {
      query: {
        enabled:
          !!organizationId &&
          !!workspaceId &&
          !!bookName &&
          !!latestBook?.version,
      },
    },
  );

  const book = bookData?.data?.book ?? latestBook;

  return {
    icon: convertToDataUrl(book?.icon),
    displayName: book ? getIntegrationDisplayName(book) : (bookName ?? ''),
    isLoading: isLoadingBooks || isLoadingBook,
  };
}
